import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@components/ui/Button';
import { Input } from '@components/ui/Input';
import { useAuthStore } from '@/stores/authStore';
import { BackendError } from '@/api/adapter/client';
import {
  changePassword,
  enrollPasskey,
  getPasskeys,
  removePasskey,
  supportsPasskeys,
} from '@/api/adapter/session';
import { validatePassword } from '@/lib/auth-validation';
import { SectionCard } from './SectionCard';
import { SectionRow } from './SectionRow';

type Action =
  | { kind: 'password' }
  | { kind: 'add' }
  | { kind: 'remove'; id: string };
export const SecuritySection = () => {
  const principal = useAuthStore((state) => state.session?.principalId);
  const keys = useQuery({
    queryKey: ['account-passkeys', principal],
    queryFn: ({ signal }) => getPasskeys(signal),
    enabled: !!principal,
    retry: false,
  });
  const [action, setAction] = useState<Action | null>(null);
  const [password, setPassword] = useState('');
  const [replacement, setReplacement] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const pending = useRef<AbortController | null>(null);
  useEffect(() => () => pending.current?.abort(), []);
  function clearPasswords() {
    setPassword('');
    setReplacement('');
    setConfirmation('');
  }
  function open(next: Action | null) {
    clearPasswords();
    setError('');
    setMessage('');
    setAction(next);
  }
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!action || busy) return;
    setError('');
    setMessage('');
    try {
      validatePassword(password);
      if (action.kind === 'password') {
        validatePassword(replacement, true);
        if (replacement !== confirmation)
          throw new Error('New passwords do not match.');
      }
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : 'Check the passwords.'
      );
      return;
    }
    const currentPassword = password,
      newPassword = replacement;
    clearPasswords();
    const controller = new AbortController();
    pending.current = controller;
    setBusy(true);
    try {
      if (action.kind === 'password')
        await changePassword(currentPassword, newPassword, controller.signal);
      else if (action.kind === 'add')
        await enrollPasskey(currentPassword, controller.signal);
      else await removePasskey(action.id, currentPassword, controller.signal);
      if (controller.signal.aborted) return;
      setMessage(
        action.kind === 'password'
          ? 'Password changed. Other sessions are signed out; this session and your wallet remain unchanged.'
          : action.kind === 'add'
            ? 'Passkey added. Use it with your account username at login.'
            : 'Passkey removed from this account. Password login remains available; your authenticator may still retain its local credential.'
      );
      setAction(null);
    } catch (reason) {
      if (controller.signal.aborted) return;
      let text =
        reason instanceof Error
          ? reason.message
          : 'The security change could not be confirmed.';
      if (
        action.kind === 'password' &&
        reason instanceof BackendError &&
        (!reason.statusCode || reason.statusCode >= 500)
      )
        text +=
          ' The password may already have changed. Check by signing in with the new password before repeating the change.';
      else if (action.kind !== 'password')
        text +=
          ' Refresh the passkey list to check the result. Password login remains available.';
      setError(text);
    } finally {
      if (!controller.signal.aborted) {
        setBusy(false);
        pending.current = null;
        if (action.kind !== 'password') void keys.refetch();
      }
    }
  }
  return (
    <SectionCard
      title="Security"
      description="Manage password and passkey access to this account."
    >
      <SectionRow
        label="Password"
        description="A new password signs out your other sessions. It does not change your custodial wallet."
        rightAlign
      >
        <Button
          variant="outline"
          disabled={busy}
          onClick={() => open({ kind: 'password' })}
        >
          Change password
        </Button>
      </SectionRow>
      <SectionRow
        label="Passkeys"
        description="An alternative to password login, using your account username. This is not a second authentication step."
        rightAlign
      >
        <Button
          variant="outline"
          disabled={
            busy || !supportsPasskeys() || keys.isError || keys.isPending
          }
          onClick={() => open({ kind: 'add' })}
        >
          Add passkey
        </Button>
      </SectionRow>
      {!supportsPasskeys() && (
        <p>
          Passkeys are unavailable in this browser. Password login and removing
          an existing passkey remain available.
        </p>
      )}
      {keys.isPending && <p role="status">Loading passkeys…</p>}
      {keys.isError && (
        <p role="alert">Could not refresh passkeys: {keys.error.message}</p>
      )}
      {keys.data?.length === 0 && !keys.isError && (
        <p>No registered passkeys.</p>
      )}
      {keys.data?.map((key, index) => (
        <SectionRow
          key={key.id}
          label={'Passkey ' + (index + 1)}
          description={
            <>
              <time dateTime={new Date(key.createdAt * 1000).toISOString()}>
                Registered {new Date(key.createdAt * 1000).toLocaleString()}
              </time>
              <p className="break-all text-xs">{key.id}</p>
            </>
          }
          rightAlign
        >
          <Button
            variant="outline"
            disabled={busy || keys.isError}
            onClick={() => open({ kind: 'remove', id: key.id })}
            aria-label={'Remove passkey ' + (index + 1)}
          >
            Remove
          </Button>
        </SectionRow>
      ))}
      <Button
        variant="link"
        disabled={busy || keys.isFetching}
        onClick={() => void keys.refetch()}
      >
        Refresh passkeys
      </Button>
      {action && (
        <form
          onSubmit={(event) => void submit(event)}
          className="mt-6 space-y-4"
          aria-label={
            action.kind === 'password'
              ? 'Change password'
              : action.kind === 'add'
                ? 'Add passkey'
                : 'Remove passkey'
          }
        >
          <h3 className="font-semibold">
            {action.kind === 'password'
              ? 'Change password'
              : action.kind === 'add'
                ? 'Add a passkey'
                : 'Remove this passkey'}
          </h3>
          {action.kind === 'remove' && (
            <p className="break-all">
              Remove {action.id} from this account? Password login will remain
              available.
            </p>
          )}
          <Input
            id="security-current-password"
            type="password"
            label="Current password"
            autoFocus
            autoComplete="current-password"
            required
            disabled={busy}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
          {action.kind === 'password' && (
            <>
              <Input
                id="security-new-password"
                type="password"
                label="New password"
                autoComplete="new-password"
                helperText="12–1024 UTF-8 bytes; spaces are preserved."
                required
                disabled={busy}
                value={replacement}
                onChange={(event) => setReplacement(event.target.value)}
              />
              <Input
                id="security-confirm-password"
                type="password"
                label="Confirm new password"
                autoComplete="new-password"
                required
                disabled={busy}
                value={confirmation}
                onChange={(event) => setConfirmation(event.target.value)}
              />
            </>
          )}
          <div className="flex flex-wrap gap-3">
            <Button type="submit" disabled={busy}>
              {busy
                ? 'Confirming…'
                : action.kind === 'password'
                  ? 'Confirm password change'
                  : action.kind === 'add'
                    ? 'Confirm passkey enrollment'
                    : 'Confirm passkey removal'}
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={busy}
              onClick={() => open(null)}
            >
              Cancel
            </Button>
          </div>
        </form>
      )}
      {error && (
        <p role="alert" className="mt-4">
          {error}
        </p>
      )}
      {message && (
        <p role="status" className="mt-4">
          {message}
        </p>
      )}
      <SectionRow
        label="Two-factor authentication"
        description="A mandatory second authentication step is not supported by the current backend."
      >
        <span>Unavailable</span>
      </SectionRow>
      <SectionRow
        label="Sessions"
        description="Use Log out in the sidebar to end this browser session. Changing your password revokes other sessions; a device list or individual session controls are unavailable."
      >
        <span>No device list available</span>
      </SectionRow>
    </SectionCard>
  );
};
